import React from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import type { DemoRoute } from '../demoMode';
import type { FeatureTour } from '../tourTypes';

const MatchingRequestDetails = React.lazy(() => import('../../matching/MatchingRequestDetails'));

// Invented demo data only — no real person or agency.
const DEMO_REQUEST_ID = 'demo-anfrage-1';

/**
 * The real request page reads its id from the route. The preview runs at
 * /feature-preview/<key>, so a descendant route hands it the demo id without
 * changing the browser URL.
 */
const MatchingPreviewPage: React.FC = () => {
  const { pathname } = useLocation();
  // Fresh demo state per preview mount (status changes / proposals of a
  // previous run must not linger).
  React.useState(resetDemoState);
  return React.createElement(
    Routes,
    { location: `${pathname.replace(/\/$/, '')}/${DEMO_REQUEST_ID}` },
    React.createElement(Route, { path: ':id', element: React.createElement(MatchingRequestDetails) }),
  );
};

const daysAgo = (d: number) => new Date(Date.now() - d * 86400_000).toISOString();
const daysAhead = (d: number) => new Date(Date.now() + d * 86400_000).toISOString().slice(0, 10);

const CRITERIA = [
  'Deutschkenntnisse mindestens B2',
  'Führerschein Klasse B',
  'Nichtraucherin',
  'Erfahrung mit Demenz',
  'Hilfe beim Transfer vom Bett in den Rollstuhl',
];

const applicant = (
  id: string,
  firstName: string,
  lastName: string,
  citizenship: string,
  hours: number,
  responseType: 'accepted' | 'rejected',
  matchStatus: string | null,
  proposedBy: 'caregiver' | 'recruiter',
  data: Record<string, any>,
  answers: string[],
  notes: string | null,
) => ({
  id,
  responseType,
  notes,
  matchStatus,
  proposedBy,
  proposedByUserId: null,
  respondedAt: new Date(Date.now() - hours * 3600_000).toISOString(),
  createdAt: new Date(Date.now() - hours * 3600_000).toISOString(),
  caregiverData: {
    firstName,
    lastName,
    phoneNumberMobile: '+40 7xx xxx xxx',
    citizenship,
    ...data,
    ...Object.fromEntries(CRITERIA.map((label, i) => [label, answers[i]])),
  },
});

const RESPONSES = [
  applicant('r1', 'Ana', 'Popescu', 'Rumänien', 3, 'accepted', 'vom_recruiter_vorgeschlagen', 'caregiver',
    { germanProficiency: 'B2', totalYearsExperience: 8, hasDrivingLicense: true, isSmoker: false, dementiaExperience: true },
    ['Ja, B2-Zertifikat', 'Ja, seit 12 Jahren', 'Ja', 'Ja, 4 Jahre bei einer Dame mit Alzheimer', 'Ja, auch mit Lifter'],
    'Ich kann ab dem 1. des Monats anreisen und bleibe gerne für längere Turnusse.'),
  applicant('r2', 'Jana', 'Kováčová', 'Slowakei', 9, 'accepted', 'interessiert', 'caregiver',
    { germanProficiency: 'C1', totalYearsExperience: 11, hasDrivingLicense: true, isSmoker: false, dementiaExperience: true },
    ['Ja, C1', 'Ja', 'Ja', 'Ja, mehrere Klientinnen', 'Ja'],
    'Graz kenne ich gut, ich war dort schon drei Jahre im Einsatz.'),
  applicant('r3', 'Ilona', 'Varga', 'Ungarn', 26, 'accepted', null, 'recruiter',
    { germanProficiency: 'B1', totalYearsExperience: 5, hasDrivingLicense: false, isSmoker: false, dementiaExperience: true },
    ['B1, besuche gerade einen Kurs', 'Nein', 'Ja', 'Ja, 2 Jahre', 'Ja'],
    null),
  applicant('r4', 'Mirela', 'Ionescu', 'Rumänien', 30, 'accepted', 'interessiert', 'caregiver',
    { germanProficiency: 'B2', totalYearsExperience: 6, hasDrivingLicense: true, isSmoker: true, dementiaExperience: false },
    ['Ja', 'Ja', 'Nein', 'Wenig', 'Ja'],
    null),
  applicant('r5', 'Katarína', 'Horváthová', 'Slowakei', 50, 'rejected', 'abgelehnt', 'caregiver',
    { germanProficiency: 'B2', totalYearsExperience: 3, hasDrivingLicense: true, isSmoker: false, dementiaExperience: false },
    ['Ja', 'Ja', 'Ja', 'Nein', 'Nein'],
    'Leider erst ab Sommer verfügbar.'),
];

const REQUEST = {
  id: DEMO_REQUEST_ID,
  patient: {
    firstName: 'Josefine',
    lastName: 'Huber',
    clientId: 'K-1042',
    caregiverMatching1: CRITERIA[0],
    caregiverMatching2: CRITERIA[1],
    caregiverMatching3: CRITERIA[2],
    caregiverMatching4: CRITERIA[3],
    caregiverMatching5: CRITERIA[4],
    addressCity: 'Graz',
    addressPostalCode: '8010',
  },
  title: '24-Stunden-Betreuung in Graz – Dame, 84 Jahre',
  description:
    'Rüstige Dame mit beginnender Demenz, lebt allein in einer Wohnung mit Lift. Unterstützung bei Körperpflege, Haushalt und Arztbesuchen. Eigenes Zimmer mit WLAN.',
  status: 'active' as const,
  createdAt: daysAgo(3),
  linkExpirationDays: 14,
  selectedFields: [
    'caregiverMatching1', 'caregiverMatching2', 'caregiverMatching3', 'caregiverMatching4', 'caregiverMatching5',
    'age', 'addressCity', 'careLevel', 'mobility', 'livingSituation', 'hasPets',
  ],
  anonymousProfileData: {},
  invitations: [],
  isPublic: true,
  publicToken: 'demo-graz-huber',
  caseInfo: {
    caseNumber: '2026-118',
    startDate: daysAhead(10),
    endDate: daysAhead(38),
    turnusLabel: '4 Wochen',
    city: 'Graz',
    postalCode: '8010',
  },
  caregiverFieldsRequired: [
    'firstName', 'lastName', 'phoneNumberMobile', 'citizenship', 'germanProficiency',
    'totalYearsExperience', 'hasDrivingLicense', 'isSmoker', 'dementiaExperience',
  ],
  publicResponses: RESPONSES,
  statistics: { total: 5, accepted: 4, rejected: 1, pending: 0 },
};

// Caregiver pool for the "Betreuer vorschlagen" dialog — must not fall through
// to the real /caregivers list.
const caregiver = (id: string, firstName: string, lastName: string) => ({
  id, firstName, lastName, email: `${firstName.toLowerCase()}.${id}@example.com`, isActive: true,
});
const CAREGIVERS = [
  caregiver('c1', 'Ana', 'Popescu'),
  caregiver('c2', 'Jana', 'Kováčová'),
  caregiver('c3', 'Ilona', 'Varga'),
  caregiver('c4', 'Mirela', 'Ionescu'),
  caregiver('c5', 'Lucia', 'Novak'),
];

// Mutable copy so a status change or a proposal survives the page's refetch
// (the generic write reply would leave the old status on screen).
let responses = RESPONSES.map((r) => ({ ...r }));
function resetDemoState() {
  responses = RESPONSES.map((r) => ({ ...r }));
}
const currentRequest = () => {
  const accepted = responses.filter((r) => r.responseType === 'accepted').length;
  return {
    ...REQUEST,
    publicResponses: responses,
    statistics: { total: responses.length, accepted, rejected: responses.length - accepted, pending: 0 },
  };
};

const mocks: DemoRoute[] = [
  { path: /^\/matching\/[^/]+$/, reply: () => currentRequest() },
  { path: /^\/matching\/[^/]+\/public-responses$/, reply: () => responses },
  {
    method: 'put',
    path: /^\/matching\/response\/([^/]+)\/match-status$/,
    reply: ({ match, body }) => {
      responses = responses.map((r) => (r.id === match[1] ? { ...r, matchStatus: body?.matchStatus ?? null } : r));
      return responses.find((r) => r.id === match[1]) || null;
    },
  },
  {
    method: 'post',
    path: /^\/matching\/[^/]+\/recruiter-proposal$/,
    delayMs: 400,
    reply: ({ body }) => {
      const cg = CAREGIVERS.find((c) => c.id === body?.caregiverId) || CAREGIVERS[0];
      const answers = CRITERIA.map((label) => body?.criteriaAnswers?.[label] || '');
      const created = applicant(`demo-prop-${responses.length + 1}`, cg.firstName, cg.lastName, 'Rumänien', 0,
        'accepted', 'vom_recruiter_vorgeschlagen', 'recruiter', {}, answers, null);
      responses = [created, ...responses];
      return created;
    },
  },
  { path: /^\/matching$/, reply: () => [] },
  { path: /^\/caregivers$/, reply: () => ({ data: CAREGIVERS, total: CAREGIVERS.length, page: 1, limit: CAREGIVERS.length, totalPages: 1 }) },
];

const k = (key: string) => `featurePreview.matching.${key}`;

export const matchingTour: FeatureTour = {
  Page: MatchingPreviewPage,
  mocks,
  chapters: [
    {
      title: k('ch1'),
      steps: [
        { at: 0.5, target: 'mt-request', balloon: { title: k('tourRequestTitle'), text: k('tourRequestText') } },
        { at: 6, target: 'mt-stats', balloon: { title: k('tourStatsTitle'), text: k('tourStatsText') } },
      ],
      duration: 11,
    },
    {
      title: k('ch2'),
      steps: [
        { at: 0.5, target: 'mt-responses', balloon: { title: k('tourResponsesTitle'), text: k('tourResponsesText') } },
        { at: 4.5, target: 'mt-response', action: 'click' },
        { at: 5.5, target: 'mt-match-status', balloon: { title: k('tourAnswersTitle'), text: k('tourAnswersText') } },
        { at: 10, target: 'mt-propose', balloon: { title: k('tourProposeTitle'), text: k('tourProposeText') } },
      ],
      duration: 14,
    },
    {
      title: k('ch3'),
      steps: [
        { at: 0.5, target: 'mt-public-link', balloon: { title: k('hs3Title'), text: k('hs3Text') } },
        { at: 5, target: 'mt-share', balloon: { title: k('tourShareTitle'), text: k('tourShareText') } },
      ],
      duration: 10,
    },
  ],
  savings: {
    aLabel: k('savA'), aDefault: 8, aMax: 60,
    bLabel: k('savB'), b: 3,
    toHoursPerMonth: (a, b) => a * b,
  },
};
